import { useQuery } from "@tanstack/react-query";
import apiClient from "../apiClient";
import type {
  DSOSchema,
  DSOTimeSeriesSchema,
  CustomerScoreSchema,
  CollectionPerformanceSchema,
} from "../../utils/types";

type ChartManagerFilters = {
  collectorId?: number;
  customerGroupId?: number;
};

function appendChartManagerFilters(
  params: URLSearchParams,
  managerFilters?: ChartManagerFilters,
) {
  if (managerFilters?.collectorId != null) {
    params.set("collector_id", String(managerFilters.collectorId));
  }
  if (managerFilters?.customerGroupId != null) {
    params.set("customer_group_id", String(managerFilters.customerGroupId));
  }
}

export const useOrganizationDSO = (
  currencyType: string,
  startDate?: string,
  endDate?: string,
  managerFilters?: ChartManagerFilters,
  consolidated?: boolean,
) => {
  return useQuery({
    queryKey: [
      "organization-dso",
      currencyType,
      startDate,
      endDate,
      managerFilters,
      consolidated ?? false,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ currency_type: currencyType });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (consolidated) params.set("consolidated", "true");
      appendChartManagerFilters(params, managerFilters);

      const response = await apiClient.get<DSOSchema>(
        `/organization/dso?${params.toString()}`,
      );
      return response.data;
    },
    enabled: !!currencyType,
  });
};

export const useOrganizationDSOTimeSeries = (
  currencyType: string,
  startDate?: string,
  endDate?: string,
  managerFilters?: ChartManagerFilters,
  consolidated?: boolean,
  formula: "classic" | "calendar_month" = "classic",
) => {
  return useQuery({
    queryKey: [
      "organization-dso-time-series",
      currencyType,
      startDate,
      endDate,
      managerFilters,
      consolidated ?? false,
      formula,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ currency_type: currencyType });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (consolidated) params.set("consolidated", "true");
      if (formula !== "classic") params.set("formula", formula);
      appendChartManagerFilters(params, managerFilters);

      const response = await apiClient.get<DSOTimeSeriesSchema>(
        `/organization/dso/time-series?${params.toString()}`,
      );
      return response.data;
    },
    enabled: !!currencyType,
  });
};

export const useCustomerDSO = (
  customerId: number,
  currencyType: string,
  startDate?: string,
  endDate?: string,
) => {
  return useQuery({
    queryKey: ["customer-dso", customerId, currencyType, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ currency_type: currencyType });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await apiClient.get<DSOSchema>(
        `/customer_representatives/${customerId}/dso?${params.toString()}`,
      );
      return response.data;
    },
    enabled: !!customerId && !!currencyType,
  });
};

export const useCustomerDSOTimeSeries = (
  customerId: number,
  currencyType: string,
  startDate?: string,
  endDate?: string,
) => {
  return useQuery({
    queryKey: [
      "customer-dso-time-series",
      customerId,
      currencyType,
      startDate,
      endDate,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ currency_type: currencyType });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await apiClient.get<DSOTimeSeriesSchema>(
        `/customer_representatives/${customerId}/dso/time-series?${params.toString()}`,
      );
      return response.data;
    },
    enabled: !!customerId && !!currencyType,
  });
};

export const useCustomerScore = (customerId: number) => {
  return useQuery({
    queryKey: ["customer-score", customerId],
    queryFn: async () => {
      const response = await apiClient.get<CustomerScoreSchema>(
        `/customer_representatives/${customerId}/score`,
      );
      return response.data;
    },
    enabled: !!customerId,
  });
};

export const useAllCustomerScores = () => {
  return useQuery({
    queryKey: ["all-customer-scores"],
    queryFn: async () => {
      const response = await apiClient.get<CustomerScoreSchema[]>(
        `/customer_representatives/scores`,
      );
      return response.data;
    },
  });
};

export const useCollectionPerformance = (
  currencyType: string,
  startDate?: string,
  endDate?: string,
  grouping: string = "month",
  dateMode: string = "issue_date",
  managerFilters?: ChartManagerFilters,
  consolidated?: boolean,
) => {
  return useQuery({
    queryKey: [
      "collection-performance",
      currencyType,
      startDate,
      endDate,
      grouping,
      dateMode,
      managerFilters,
      consolidated ?? false,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        currency_type: currencyType,
        grouping: grouping,
        date_mode: dateMode,
      });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (consolidated) params.set("consolidated", "true");
      appendChartManagerFilters(params, managerFilters);

      const response = await apiClient.get<CollectionPerformanceSchema>(
        `/organization/collection-performance?${params.toString()}`,
      );
      return response.data;
    },
    enabled: !!currencyType,
  });
};
